import { i18n } from '@fs/zion-locale'

export default {
  collectionIdsTitle: i18n.t('searchSuggestions.collectionIdsTitle', 'Collection Filters'),
  collectionIdsSuggestion: i18n.t(
    'searchSuggestions.collectionIdsSuggestion',
    'Collection filters will return image groups that belong to the selected collections. However, the collection(s) applied to your search cannot be found. Consider removing any collection filters to expand your search.'
  ),
  collectionIdsButton: i18n.t('searchSuggestions.collectionsIdsButton', 'Remove Collection Filters'),
  cameraOperatorSuggestion: i18n.t(
    'cameraOperatorSuggestion',
    'Only results that match all search terms will be returned, so using multiple fields can limit your search results. Consider searching without the camera operator ID.'
  ),
  cameraOperatorTitle: i18n.t('cameraOperatorTitle', 'Try searching without Camera Operator ID.'),
  cameraOperatorButton: i18n.t('cameraOperatorButton', 'Remove Camera Operator ID'),
  communityTitle: () => i18n.t('communityTitle', 'Community Help'),
  communityParagraph: () =>
    i18n.t(
      'joinACommunityParagraph',
      'Join a group in the <communityLink>FamilySearch online community</communityLink>.'
    ),
  communityLinkText: () => i18n.t('communityLinkText', 'FamilySearch online community'),
  creatorSuggestion: i18n.t(
    'creatorSuggestion',
    'Only results that match all search terms will be returned, so using multiple fields can limit your search results. Consider leaving the creator field blank.'
  ),
  creatorTitle: i18n.t('creatorTitle', 'Try searching without creator.'),
  creatorButton: i18n.t('creatorButton', 'Remove Creator'),
  custodianSuggestion: i18n.t(
    'custodianSuggestion',
    'Only results that match all search terms will be returned, so using multiple fields can limit your search results. Consider leaving the custodian field blank.'
  ),
  custodianTitle: i18n.t('custodianTitle', 'Try searching without custodian'),
  custodianButton: () => i18n.t('custodianButton', 'Remove Custodian'),
  dateSuggestion: i18n.t(
    'dateSuggestion',
    'The record you are looking for may be missing a date. Try leaving the date field blank.'
  ),
  dateTitle: i18n.t('dateTitle', 'Try removing the date.'),
  dateButton: i18n.t('dateButton', 'Remove Date'),
  dateTooSoonSuggestion: i18n.t(
    'dateTooSoonSuggestion',
    'The date may be too recent. Newer documents may not be shown due to privacy concerns.'
  ),
  example: () => i18n.t('example', 'Example'),
  imageGroupNumberSuggestion: i18n.t(
    'imageGroupNumberSuggestion',
    'Each result has a unique image group number; only results that match all search terms will be returned. Consider searching without the image group number.'
  ),
  imageGroupNumberTitle: i18n.t('imageGroupNumberTitle', 'Try removing Image Group Number.'),
  imageGroupNumberButton: i18n.t('imageGroupNumberButton', 'Remove Image Group Number (DGS/GS)'),
  keywordSuggestion: () =>
    i18n.t(
      'keywordSuggestion',
      'The Keyword field searches the Title, Volume, Archival Reference Number, Creator, and Custodian columns. To expand your search, try searching the keyword all by itself and leaving the other fields blank.'
    ),
  keywordTip: () =>
    i18n.t(
      'keywordTip',
      "If a surname doesn't work, try searching for just the first letter of the surname  or adding an '*' after the first letter of the surname or keyword you're looking for."
    ),
  keywordTitle: () => i18n.t('keywordTitle', 'Try a different keyword.'),
  keywordButton: () => i18n.t('keywordButton', 'Remove Keyword'),
  // this is inside a Trans component, this is just the string
  lifeEventSuggestion:
    'Some results may be classified differently than you expect. For example, the Education life event contains School Records, and the Residence life event contains School Census Records. To expand your search, set the life event to <bold>All</bold>.',
  lifeEventTitle: i18n.t('lifeEventTitle', 'Try searching all life events.'),
  lifeEventButton: i18n.t('lifeEventButton', 'Set Life Event to All'),
  mapButton: () => i18n.t('mapSuggestionButton', 'View Map'),
  mapSuggestion: () =>
    i18n.t(
      'mapSuggestion',
      'Explore nearby results on the map. This will also toggle on "Show map in Refine Search" in the Options panel.'
    ),
  mapTitle: () => i18n.t('mapTitle', 'Nearby results'),
  mediaNumberSuggestion: i18n.t(
    'mediaNumberSuggestions',
    "The media number field requires a project ID number. If you're not finding what you're looking for, consider leaving these fields blank and using a different search field."
  ),
  mediaNumberTitle: i18n.t('mediaNumberTitle', 'Try searching without Media Number.'),
  mediaNumberButton: i18n.t('mediaNumberButton', 'Remove Media Number'),
  naturalGroupIdsSuggestion: i18n.t(
    'naturalGroupIdsSuggestion',
    'A natural group ID will return a specific result. Be careful when using Natural Group IDs with any other field.'
  ),
  naturalGroupIdsTitle: i18n.t('naturalGroupIdsTitle', 'Try removing Natural Group IDs.'),
  naturalGroupIdsButton: i18n.t('naturalGroupIdsButton', 'Remove Natural Group IDs'),
  noResultsHeading: () => i18n.t('SearchSuggestions.noResultsHeading', 'No results found. Try these tips:'),
  period: () => i18n.t('period', '.'),
  placeAction: (place) => i18n.t('placeAction', 'Search {place}', { place }),
  placeExample: () =>
    i18n.t(
      'placeExample',
      'When camera operators encounter a book that has 4 cities, they must specify a location that encompasses all of the cities. For example, if all of the cities were within a county, then the county would be chosen.'
    ),
  placeSuggestion: (parentPlaceName, placeName) =>
    i18n.t(
      'placeParentSuggestion',
      'Explore search results for {parentPlaceName}, the place that surrounds {placeName}.',
      { parentPlaceName, placeName }
    ),
  placeTip: () =>
    i18n.t(
      'placeTip',
      'When you come across results with multiple inner places, use the multiple image view to look for images that change color or form, since this might indicate where the place, date, or type of image changes. Sometimes changes are marked by a black or white titleboard, but not always.'
    ),
  placeTitle: () => i18n.t('placeTitle', 'Try a larger place.'),
  projectIdSuggestion: i18n.t(
    'projectIdSuggestion',
    'Only results that match all search terms will be returned, so using multiple fields can limit your search results. Consider leaving the project ID field blank.'
  ),
  projectIdTitle: i18n.t('projectIdTitle', 'Try searching without project ID.'),
  projectIdButton: i18n.t('projectIdButton', 'Remove Project ID'),
  recordTypeSuggestion:
    'The record you are looking for could have been archived under a different category. For example, baptism records are sometimes described as church records, and birth records are sometimes described as birth affidavits. To see results that are not labeled with a record type, clear the Record Type field, and set the Life Event field to <bold>All</bold>.',
  recordTypeTitle: i18n.t('recordTypeTitle', 'Try a different record type.'),
  recordTypeButton: i18n.t('recordTypeButton', 'Remove Record Type'),
  removeButton: () => i18n.t('common-ui:remove.action', 'Remove'),
  searchButton: () => i18n.t('common-ui:search.action', 'Search'),
  tip: () => i18n.t('tip', 'Tip'),
  tryTheseTipsHeading: () => i18n.t('SearchSuggestions.tryTheseTips', 'Try These Tips:'),
}
